import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { errorMessageFor } from '../../auth/error-message';
import { formatDate, formatKg } from '../../lib/format';
import { BarChart } from '../../training/charts/BarChart';
import '../admin.css';

const SESSION_STATUS_LABELS = { completed: 'Completada', in_progress: 'En curso' };

/**
 * "Progreso" tab of a member's admin detail (plan Task 7): metric cards
 * (total completed sessions, this week's training days vs the profile's
 * target), the weekly `BarChart` (days per week, with a target line), the
 * personal-record list and the most recent sessions — every number comes
 * straight from `GET /members/:id/progress` and `GET /members/:id/sessions`
 * (`progress-service.js`) and is rendered as-is; this component does no
 * arithmetic of its own (spec: "el cliente nunca recalcula métricas").
 */
export function MemberProgressSection({ memberId, api }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({
    status: 'loading', progress: null, sessions: [], message: '',
  });

  useEffect(() => {
    let active = true;
    setState({
      status: 'loading', progress: null, sessions: [], message: '',
    });
    Promise.all([api.getMemberProgress(memberId), api.listMemberSessions(memberId)])
      .then(([progress, sessions]) => {
        if (!active) return;
        setState({
          status: 'ready', progress, sessions: sessions || [], message: '',
        });
      })
      .catch((error) => {
        if (!active) return;
        const outcome = handleApiError(error);
        if (outcome.handled) return;
        setState({
          status: 'error', progress: null, sessions: [], message: errorMessageFor(error),
        });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, memberId, handleApiError]);

  if (state.status === 'loading') {
    return (
      <div className="gym-state gym-state--loading" role="status" aria-live="polite">
        <span className="gym-state__spinner" aria-hidden="true" />
        <p className="gym-state__text">Cargando progreso…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return <p role="alert" className="gym-form__error">{state.message}</p>;
  }

  const { progress, sessions } = state;
  const weekly = progress.weekly || [];
  const currentWeek = weekly[weekly.length - 1] || null;
  const hasTarget = typeof progress.targetDaysPerWeek === 'number';
  const personalRecords = progress.personalRecords || [];

  return (
    <div className="gym-admin-progress-section">
      <div className="gym-admin-progress-cards">
        <div className="gym-metric-card">
          <p className="gym-metric-card__label">Sesiones completadas</p>
          <p className="gym-metric-card__value">{progress.sessionsCount}</p>
        </div>
        <div className="gym-metric-card">
          <p className="gym-metric-card__label">Días esta semana</p>
          <p className="gym-metric-card__value">
            {hasTarget ? `${currentWeek ? currentWeek.trainingDays : 0} / ${progress.targetDaysPerWeek}` : (currentWeek ? currentWeek.trainingDays : 0)}
          </p>
          {!hasTarget && <p className="gym-admin-field-hint">Sin objetivo definido</p>}
        </div>
      </div>

      <div className="gym-card">
        <BarChart
          data={weekly.map((week) => ({ label: formatDate(week.weekStart), value: week.trainingDays }))}
          title="Días de entrenamiento por semana"
          unit="días"
          target={hasTarget ? progress.targetDaysPerWeek : undefined}
        />
      </div>

      <div className="gym-card">
        <h2 className="gym-card__title">Récords personales</h2>
        {personalRecords.length === 0 ? (
          <p className="gym-muted-text">Todavía no hay récords registrados.</p>
        ) : (
          <ul className="gym-admin-progress-pr-list">
            {personalRecords.map((pr) => (
              <li key={pr.exerciseId}>
                <span className="gym-admin-progress-pr-list__name">{pr.exerciseName}</span>
                <span className="gym-admin-progress-pr-list__value">{formatKg(pr.bestWeightKg)}</span>
                <span className="gym-admin-progress-pr-list__date">{formatDate(pr.performedOn)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="gym-card">
        <h2 className="gym-card__title">Últimas sesiones</h2>
        {sessions.length === 0 ? (
          <p className="gym-muted-text">Todavía no hay sesiones registradas.</p>
        ) : (
          <ul className="gym-admin-progress-sessions-list">
            {sessions.map((session) => (
              <li key={session.id}>
                <span>{formatDate(session.performedOn)}</span>
                <span>{session.dayName || 'Entrenamiento'}</span>
                <span className={`gym-admin-progress-sessions-list__status gym-admin-progress-sessions-list__status--${session.status === 'completed' ? 'done' : 'open'}`}>{SESSION_STATUS_LABELS[session.status] || session.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
